import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { saveSelectionAPI } from "../api/getApi";
import {
  loadApCollegesYear,
  loadTsCollegesYear,
  loadApPharmacyColleges,
  loadTsPharmacyColleges,
  logCollegeSearchSubmission,
} from "../api/getApi";
import { useAuth } from "../context/authContext";
import Modal from "../components/modal";
import { Helmet } from "react-helmet-async";
import ConfirmModal from "../components/confirmModal";
import {
  getRankKey,
  handleSearch,
  handleTypeFilterChange,
  addCollege,
  removeCollege,
  moveCollegeUp,
  moveCollegeDown,
  sortSelectedCollegesByRank,
  applyFilters,
} from "../actions/actions";
import SafetyMarginSlider from "../components/safetyMarginSlider";
import SearchInput from "../components/searchInput";
import TypeCheckboxes from "../components/typeCheckboxes";
import CollegeList from "../components/collegeList";
import SelectedList from "../components/selectedList";
import WarningAlert from "../components/warningAlert";
import Text from "../components/text";
import CollegeGuideForm from "../components/input"

const LAST_SEARCH_CRITERIA_KEY = "lastUserSearchCriteria";

const CollegeGuide = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user } = useAuth();

  const inputDataFromHome = useMemo(
    () => location.state?.inputData || {},
    [location.state?.inputData]
  );

  const isInitialized = useRef(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [formData, setFormData] = useState({
    rank: inputDataFromHome.rank || "",
    gender: inputDataFromHome.gender || "Male",
    caste: inputDataFromHome.caste || "oc",
    examType: inputDataFromHome.examType || "ap",
    year: inputDataFromHome.year || "2023",
    location: inputDataFromHome.location || "",
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [submittedFormData, setSubmittedFormData] = useState({
    rank: "",
    gender: "",
    caste: "",
    examType: "",
    year: "",
    location: "",
  });
  const [selectedColleges, setSelectedColleges] = useState([]);
  const [colleges, setColleges] = useState([]);
  const [filteredColleges, setFilteredColleges] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [isSaved, setIsSaved] = useState(false);
  const [safetyMargin, setSafetyMargin] = useState(10);
  const [selectedPlaces, setSelectedPlaces] = useState([]);
  const [selectedBranches, setSelectedBranches] = useState([]);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [showPlaceDropdown, setShowPlaceDropdown] = useState(false);
  const [showBranchDropdown, setShowBranchDropdown] = useState(false);
  const [placeSearchTerm, setPlaceSearchTerm] = useState("");
  const [branchSearchTerm, setBranchSearchTerm] = useState("");
  const [isRankSorted, setIsRankSorted] = useState(false);
  const [originalOrder, setOriginalOrder] = useState([]);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [isAvailableCollegesFullScreen, setIsAvailableCollegesFullScreen] =
    useState(false);
  const [isSelectedCollegesFullScreen, setIsSelectedCollegesFullScreen] =
    useState(false);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "examType" && {
        year:
          value === "ap" || value === "ap_bipc"
            ? "2023"
            : value === "ts_bipc"
            ? "first"
            : "2024_first",
      }),
    }));
    isInitialized.current = true;
  };

  const fetchCollegeData = useCallback(
    async (currentFormData) => {
      setLoading(true);
      setError("");
      try {
        let response;
        if (currentFormData.examType === "ap") {
          response = await loadApCollegesYear(currentFormData);
        } else if (currentFormData.examType === "ap_bipc") {
          response = await loadApPharmacyColleges(currentFormData);
        } else if (currentFormData.examType === "ts_bipc") {
          response = await loadTsPharmacyColleges(currentFormData);
        } else {
          response = await loadTsCollegesYear(currentFormData);
        }

        if (!response.success) {
          setError(response.error || "Failed to load colleges.");
          setColleges([]);
        } else {
          setColleges(response.data || []);
        }
      } catch (err) {
        console.error("Error in fetchCollegeData:", err);
        setError("An error occurred while fetching college data.");
        setColleges([]);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const saveSelection = async (providedName = null) => {
    const name = providedName || new Date().toLocaleString();

    if (!isAuthenticated) {
      navigate("/login", {
        state: {
          pendingFavorite: {
            name,
            colleges: selectedColleges,
            formData,
          },
          redirectAfterLogin: "/profile",
          message: "Please log in to save your college selection",
        },
      });
      return;
    }

    try {
      const data = await saveSelectionAPI(selectedColleges, formData, name);

      if (data.success) {
        setSelectedColleges([]);
        console.log(data.favoriteId);
        navigate("/review-report", {
          state: {
            selectedColleges,
            formData,
            navigationfrom: true,
            favoriteIndex: data.favoriteIndex || 0, // Change to favoriteIndex
          },
        });
      } else {
        setError("Failed to save your selection.");
      }
    } catch (error) {
      console.error("Error saving selection:", error);
      setError("An error occurred while saving your selection.");
    }
  };

  const proceed = () => {
    setIsModalOpen(true);
  };

  const handleModalSubmit = async (name) => {
    await saveSelection(name);
  };

  const handleBack = () => {
    if (selectedColleges.length > 0 && !isSaved) {
      const shouldSave = window.confirm(
        "You have unsaved changes. Do you want to save your selected colleges before going back?"
      );
      if (shouldSave) {
        saveSelection().then(() => {
          navigate(-1);
        });
      } else {
        setSelectedColleges([]);
        navigate(-1);
      }
    } else {
      navigate(-1);
    }
  };

  const handleBeforeUnload = useCallback(
    (event) => {
      if (selectedColleges.length > 0 && !isSaved) {
        event.preventDefault();
        event.returnValue =
          "You have unsaved changes. Are you sure you want to leave?";
      }
    },
    [selectedColleges, isSaved]
  );

  const handleLogCollegeSearchSubmission = useCallback(
    (dataToLog) => {
      if (isAuthenticated && dataToLog.location) {
        logCollegeSearchSubmission(dataToLog);
      }
    },
    [isAuthenticated]
  );

  const handleFormSubmit = useCallback(
    async (dataToSubmit, isAutoSubmit = false) => {
      if (!dataToSubmit) {
        setError("Form data is missing. Cannot submit.");
        setLoading(false);
        return;
      }
      if (
        !dataToSubmit.rank ||
        !dataToSubmit.gender ||
        !dataToSubmit.caste ||
        !dataToSubmit.location ||
        !dataToSubmit.examType ||
        !dataToSubmit.year
      ) {
        setError(
          "Please fill in all required fields: Rank, Gender, Category, Location, Exam Type, and Year."
        );
        setLoading(false);
        return;
      }

      if (!isAuthenticated && !isAutoSubmit) {
        navigate("/signup", {
          state: {
            pendingAction: {
              type: "submitCollegeSearchFromFilter",
              data: dataToSubmit,
            },
            redirectAfterLogin: "/filter",
          },
        });
        return;
      }

      setLoading(true);
      setError("");
      await fetchCollegeData(dataToSubmit);
      setFormSubmitted(true);
      setSubmittedFormData({ ...dataToSubmit });
      setSelectedColleges([]); // Reset selected colleges on new submission
      setLoading(false);

      if (isAuthenticated) {
        handleLogCollegeSearchSubmission(dataToSubmit);
      }
    },
    [isAuthenticated, navigate, fetchCollegeData, handleLogCollegeSearchSubmission]
  );

  // Handle auto-submission after login/signup
  useEffect(() => {
    const { formDataForAutoSubmit, autoSubmit, message } = location.state || {};
    const currentPath = location.pathname;

    if (message) {
      console.log("Message from auth page (Filter):", message);
      navigate(currentPath, { replace: true, state: { ...location.state, message: undefined } });
    }

    if (
      autoSubmit &&
      formDataForAutoSubmit &&
      isAuthenticated &&
      currentPath === "/filter"
    ) {
      if (
        formDataForAutoSubmit.rank &&
        formDataForAutoSubmit.gender &&
        formDataForAutoSubmit.caste &&
        formDataForAutoSubmit.examType &&
        formDataForAutoSubmit.year &&
        formDataForAutoSubmit.location
      ) {
        setFormData(formDataForAutoSubmit);
        handleFormSubmit(formDataForAutoSubmit, true);
      } else {
        console.warn(
          "Filter Page: Auto-submit attempted without complete form data:",
          formDataForAutoSubmit
        );
        setError("Could not auto-submit form on filter page due to incomplete data.");
      }
      navigate(currentPath, { replace: true, state: {} });
    }
  }, [location.state, isAuthenticated, navigate, handleFormSubmit]);

  // Handle auto-submit from inputDataFromHome
  useEffect(() => {
    if (
      !isInitialized.current &&
      inputDataFromHome &&
      inputDataFromHome.rank &&
      inputDataFromHome.caste &&
      inputDataFromHome.gender &&
      inputDataFromHome.location &&
      inputDataFromHome.examType &&
      inputDataFromHome.year &&
      !formSubmitted
    ) {
      setFormData((prev) => ({ ...prev, ...inputDataFromHome }));
      handleFormSubmit(inputDataFromHome, true);
      isInitialized.current = true;
    }
  }, [inputDataFromHome, formSubmitted, handleFormSubmit]);

  const handleConfirmSubmit = () => {
    if (
      !formData.rank ||
      !formData.gender ||
      !formData.caste ||
      !formData.location ||
      !formData.examType ||
      !formData.year
    ) {
      setError("Please fill in all required fields in the form before creating a list.");
      setIsConfirmModalOpen(false);
      return;
    }
    handleFormSubmit(formData); // Use current formData directly
    setIsConfirmModalOpen(false);
  };

  useEffect(() => {
    if (colleges.length > 0) {
      const applyCurrentFilters = () => {
        const filteredResults = colleges.filter((college) => {
          const rankKey = getRankKey(submittedFormData);
          const collegeRankValue = college[rankKey];

          // --- Start of Change: Rank Filtering ---
          // If the college has a numeric rank, apply the filter.
          // If the rank is "-" or "NA", it should pass this filter.
          if (collegeRankValue && /^\d+$/.test(collegeRankValue)) {
            const collegeRank = parseInt(collegeRankValue, 10);
            const studentRank = parseInt(submittedFormData.rank, 10) || 0;

            const meetsRankCriteria =
              collegeRank >= studentRank * (1 - safetyMargin / 100);

            if (!meetsRankCriteria) {
              return false;
            }
          }
          // --- End of Change: Rank Filtering ---

          const meetsSearchCriteria = searchTerm
            ? college.inst_name
                .toLowerCase()
                .includes(searchTerm.toLowerCase()) ||
              college.inst_code
                .toLowerCase()
                .includes(searchTerm.toLowerCase()) ||
              college.place.toLowerCase().includes(searchTerm.toLowerCase()) ||
              college.branch_code
                .toLowerCase()
                .includes(searchTerm.toLowerCase())
            : true;

          const meetsPlaceCriteria =
            selectedPlaces.length === 0 ||
            selectedPlaces.includes(college.place);

          const meetsBranchCriteria =
            selectedBranches.length === 0 ||
            selectedBranches.includes(college.branch_code);

          const meetsTypeCriteria =
            selectedTypes.length === 0 || selectedTypes.includes(college.type);

          return (
            meetsSearchCriteria &&
            meetsPlaceCriteria &&
            meetsBranchCriteria &&
            meetsTypeCriteria
          );
        });

        // --- Start of Change: Sorting ---
        // Sort the filtered results to place non-numeric ranks at the end
        const sortedResults = filteredResults.sort((a, b) => {
          const rankKey = getRankKey(submittedFormData);
          const rankA = a[rankKey];
          const rankB = b[rankKey];

          const isARankless = !/^\d+$/.test(rankA);
          const isBRankless = !/^\d+$/.test(rankB);

          if (isARankless && isBRankless) return 0; // Both are rankless, keep order
          if (isARankless) return 1; // A is rankless, move it to the end
          if (isBRankless) return -1; // B is rankless, move it to the end

          // Both have numeric ranks, sort them numerically
          return parseInt(rankA, 10) - parseInt(rankB, 10);
        });
        // --- End of Change: Sorting ---

        setFilteredColleges(sortedResults);
      };

      applyCurrentFilters();
    }
  }, [
    safetyMargin,
    colleges,
    submittedFormData,
    searchTerm,
    selectedPlaces,
    selectedBranches,
    selectedTypes,
  ]);

  useEffect(() => {
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [handleBeforeUnload]);

  useEffect(() => {
    const handleClickOutside = () => {
      setShowPlaceDropdown(false);
      setShowBranchDropdown(false);
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  useEffect(() => {
    const styles = `
      .safety-safe { background-color: #e8f5e9 !important; }
      .safety-moderate { background-color: #fff9c4 !important; }
      .safety-ambitious { background-color: #ffcdd2 !important; }
      .full-screen {
        position: fixed !important;
        inset: 0 !important;
        width: 100vw !important;
        height: 100dvh !important;
        z-index: 1000 !important;
        background: white !important;
        overflow: auto !important;
        padding: 20px !important;
        box-sizing: border-box !important;
      }
      .laptop-view.full-screen,
      .laptop-view1.full-screen {
        height: 100dvh !important;
        min-height: unset !important;
        max-height: unset !important;
        overflow: auto !important;
      }
    `;
    const styleElement = document.createElement("style");
    styleElement.textContent = styles;
    document.head.appendChild(styleElement);
    return () => document.head.removeChild(styleElement);
  }, []);

  const uniquePlaces = useMemo(
    () =>
      [...new Set(colleges.map((college) => college.place))].sort((a, b) =>
        a.localeCompare(b)
      ),
    [colleges]
  );

  const uniqueBranches = useMemo(
    () =>
      [...new Set(colleges.map((college) => college.branch_code))].sort(
        (a, b) => a.localeCompare(b)
      ),
    [colleges]
  );

  const filteredPlaces = uniquePlaces.filter((place) =>
    place.toLowerCase().includes(placeSearchTerm.toLowerCase())
  );

  const filteredBranches = uniqueBranches.filter((branch) =>
    branch.toLowerCase().includes(branchSearchTerm.toLowerCase())
  );

  const handleBranchFilterChange = (branch) => {
    const updatedSelectedBranches = selectedBranches.includes(branch)
      ? selectedBranches.filter((b) => b !== branch)
      : [...selectedBranches, branch];
    setSelectedBranches(updatedSelectedBranches);
    applyFilters(
      searchTerm,
      selectedPlaces,
      updatedSelectedBranches,
      selectedTypes,
      colleges,
      setFilteredColleges,
      submittedFormData,
      safetyMargin,
      getRankKey
    );
  };

  const handlePlaceFilterChange = (place) => {
    const updatedSelectedPlaces = selectedPlaces.includes(place)
      ? selectedPlaces.filter((p) => p !== place)
      : [...selectedPlaces, place];
    setSelectedPlaces(updatedSelectedPlaces);
    applyFilters(
      searchTerm,
      updatedSelectedPlaces,
      selectedBranches,
      selectedTypes,
      colleges,
      setFilteredColleges,
      submittedFormData,
      safetyMargin,
      getRankKey
    );
  };

  const toggleAvailableCollegesFullScreen = () => {
    if (isSelectedCollegesFullScreen) {
      setIsSelectedCollegesFullScreen(false);
    }
    setIsAvailableCollegesFullScreen(!isAvailableCollegesFullScreen);
  };

  const toggleSelectedCollegesFullScreen = () => {
    if (isAvailableCollegesFullScreen) {
      setIsAvailableCollegesFullScreen(false);
    }
    setIsSelectedCollegesFullScreen(!isSelectedCollegesFullScreen);
  };

  return (
    <section className="main-content bg-gray-50">
      <Helmet>
        <title>{Text.Filter.Title} | CollegeRank</title>
        <meta name="description" content={Text.Filter.Description} />
      </Helmet>
      <div className="inner-content">
        {error && (
          <div className="container mx-auto px-4 py-4">
            <div
              className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative"
              role="alert"
            >
              <span className="block sm:inline">{error}</span>
              <button
                className="absolute top-0 right-0 px-4 py-3"
                onClick={() => setError(null)}
              >
                <svg
                  className="fill-current h-6 w-6 text-red-500"
                  role="button"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                >
                  <path d="M14.348 14.849a1 1 0 01-1.414 0L10 11.414l-2.934 2.935a1 1 0 11-1.414-1.414L8.586 10 5.652 7.065a1 1 0 111.414-1.414L10 8.586l2.934-2.935a1 1 0 111.414 1.414L11.414 10l2.934 2.935a1 1 0 010 1.414z" />
                </svg>
              </button>
            </div>
          </div>
        )}
        {isAuthenticated && user && (
          <div className="container mx-auto px-4 pb-5 text-center">
            <p className="text-lg text-gray-600">
              Hello, {user.name}
              {Text.Filter.Description}
            </p>
          </div>
        )}
        <div className="container mx-auto">
          <div className="grid md:grid-cols-4 gap-8 py-8">
            <div className="md:col-span-3 colleg-guid-form">
              <CollegeGuideForm
                formData={formData}
                onChange={handleFormChange}
                onSubmit={(dataFromChildForm) => {
                  // Only show ConfirmModal if there are unsaved changes
                  if (isAuthenticated && selectedColleges.length > 0 && !isSaved) {
                    setFormData(dataFromChildForm); // Update formData before showing modal
                    setIsConfirmModalOpen(true);
                  } else {
                    handleFormSubmit(dataFromChildForm); // Proceed directly with submission
                  }
                }}
              />
            </div>
            {formSubmitted && (
              <div className="md:col-span-1 flex flex-col gap-4">
                <SafetyMarginSlider
                  safetyMargin={safetyMargin}
                  setSafetyMargin={setSafetyMargin}
                />
                <TypeCheckboxes
                  selectedTypes={selectedTypes}
                  handleTypeFilterChange={(type) =>
                    handleTypeFilterChange(
                      type,
                      selectedTypes,
                      setSelectedTypes,
                      (search, places, branches, types) =>
                        applyFilters(
                          search,
                          places,
                          branches,
                          types,
                          colleges,
                          setFilteredColleges,
                          submittedFormData,
                          safetyMargin,
                          getRankKey
                        ),
                      searchTerm,
                      selectedPlaces,
                      selectedBranches
                    )
                  }
                />
              </div>
            )}
          </div>
        </div>
        {formSubmitted && (
          <div className="container mx-auto">
            {loading ? (
              <div className="grid md:grid-cols-3 gap-8 py-8">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                <p className="mt-4 text-gray-700">{Text.Filter.Loading}</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-3 gap-8 py-8">
                <div
                  className={`md:col-span-2 ${
                    isAvailableCollegesFullScreen ? "full-screen" : ""
                  }`}
                >
                  <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-bold text-gray-800">
                      {Text.Filter.AvailableColleges}
                    </h2>
                    <button
                      onClick={toggleAvailableCollegesFullScreen}
                      className="text-blue-500 hover:text-blue-700 focus:outline-none"
                      title={
                        isAvailableCollegesFullScreen
                          ? "Exit Full Screen"
                          : "Full Screen"
                      }
                    >
                      {isAvailableCollegesFullScreen ? (
                        <svg
                          className="w-6 h-6"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      ) : (
                        <svg
                          className="w-6 h-6"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"
                          />
                        </svg>
                      )}
                    </button>
                  </div>
                  <form className="mb-4">
                    <SearchInput
                      onChange={(e) =>
                        handleSearch(
                          e,
                          setSearchTerm,
                          (search, places, branches, types) =>
                            applyFilters(
                              search,
                              places,
                              branches,
                              types,
                              colleges,
                              setFilteredColleges,
                              submittedFormData,
                              safetyMargin,
                              getRankKey
                            ),
                          selectedPlaces,
                          selectedBranches,
                          selectedTypes
                        )
                      }
                      placeholder="Search by college code, name, location or branch"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                    />
                  </form>
                  <CollegeList
                    filteredColleges={filteredColleges}
                    selectedColleges={selectedColleges}
                    addCollege={(college) =>
                      addCollege(
                        college,
                        selectedColleges,
                        setSelectedColleges,
                        setIsSaved,
                        isRankSorted,
                        setOriginalOrder
                      )
                    }
                    formData={submittedFormData}
                    showPlaceDropdown={showPlaceDropdown}
                    setShowPlaceDropdown={setShowPlaceDropdown}
                    showBranchDropdown={showBranchDropdown}
                    setShowBranchDropdown={setShowBranchDropdown}
                    placeSearchTerm={placeSearchTerm}
                    setPlaceSearchTerm={setPlaceSearchTerm}
                    branchSearchTerm={branchSearchTerm}
                    setBranchSearchTerm={setBranchSearchTerm}
                    selectedPlaces={selectedPlaces}
                    handlePlaceFilterChange={handlePlaceFilterChange}
                    selectedBranches={selectedBranches}
                    handleBranchFilterChange={handleBranchFilterChange}
                    filteredPlaces={filteredPlaces}
                    filteredBranches={filteredBranches}
                    loading={loading}
                    isFullScreen={isAvailableCollegesFullScreen}
                  />
                </div>
                <div
                  className={`md:col-span-1 ${
                    isSelectedCollegesFullScreen ? "full-screen" : ""
                  }`}
                >
                  <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-bold text-gray-800">
                      {Text.Filter.SelectedColleges}
                    </h2>
                    <button
                      onClick={toggleSelectedCollegesFullScreen}
                      className="text-blue-500 hover:text-blue-700 focus:outline-none"
                      title={
                        isSelectedCollegesFullScreen
                          ? "Exit Full Screen"
                          : "Full Screen"
                      }
                    >
                      {isSelectedCollegesFullScreen ? (
                        <svg
                          className="w-6 h-6"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      ) : (
                        <svg
                          className="w-6 h-6"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"
                          />
                        </svg>
                      )}
                    </button>
                  </div>
                  <SelectedList
                    selectedColleges={selectedColleges}
                    removeCollege={(college) =>
                      removeCollege(
                        college,
                        selectedColleges,
                        setSelectedColleges,
                        setIsSaved
                      )
                    }
                    moveCollegeUp={(index) =>
                      moveCollegeUp(
                        index,
                        selectedColleges,
                        setSelectedColleges,
                        setIsSaved
                      )
                    }
                    moveCollegeDown={(index) =>
                      moveCollegeDown(
                        index,
                        selectedColleges,
                        setSelectedColleges,
                        setIsSaved
                      )
                    }
                    sortSelectedCollegesByRank={() =>
                      sortSelectedCollegesByRank(
                        selectedColleges,
                        setSelectedColleges,
                        isRankSorted,
                        setIsRankSorted,
                        originalOrder,
                        setOriginalOrder,
                        setIsSaved,
                        submittedFormData,
                        getRankKey
                      )
                    }
                    formData={submittedFormData}
                    isRankSorted={isRankSorted}
                    setIsRankSorted={setIsRankSorted}
                    originalOrder={originalOrder}
                    setOriginalOrder={setOriginalOrder}
                    setIsSaved={setIsSaved}
                    isFullScreen={isSelectedCollegesFullScreen}
                  />
                </div>
              </div>
            )}
            <div className="grid md:grid-cols-4 gap-8 py-8">
              <div className="col-span-full">
                <WarningAlert />
              </div>
              <div className="col-span-2">
                <button
                  type="button"
                  className="w-full px-4 py-3 bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium rounded-lg transition"
                  onClick={handleBack}
                >
                  Back
                </button>
              </div>
              <div className="col-span-2">
                <button
                  type="button"
                  className="w-full px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={proceed}
                  disabled={selectedColleges.length === 0}
                >
                  Save and Download
                </button>
              </div>
            </div>
            <Modal
              isOpen={isModalOpen}
              onClose={() => setIsModalOpen(false)}
              onSubmit={handleModalSubmit}
              title="Enter a name for your list"
              placeholder="List name"
            />
            <ConfirmModal
              isOpen={isConfirmModalOpen}
              onClose={() => setIsConfirmModalOpen(false)} // Ensure modal can be closed
              onSubmit={handleConfirmSubmit}
              title="Confirm Form Submission"
              message="You have unsaved changes in your selected colleges list. Do you want to proceed with submitting the new form?"
              submitButtonText="Yes"
              cancelButtonText="No"
            />
          </div>
        )}
      </div>
    </section>
  );
};

export default CollegeGuide;